import React, { useState } from 'react';
import { Globe, Loader2, MapPin, PenLine, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import api, { formatError } from '@/lib/api';

const OPTIONS = [
  { key: 'my_city', icon: MapPin, title: 'My city', help: 'People in and around one city' },
  { key: 'major_cities', icon: Sparkles, title: 'Major cities of India', help: 'Delhi, Mumbai, Bengaluru and 7 more. Best for small budgets' },
  { key: 'whole_india', icon: Globe, title: 'All of India', help: 'Anyone in the country' },
  { key: 'custom', icon: PenLine, title: 'Somewhere else', help: 'Describe it in your own words' },
];

const EXAMPLES = ['Mumbai, Pune and Nashik', 'All of Kerala', 'Within 20 km of Koramangala, Bengaluru', 'Tier 2 cities in Gujarat'];

/** Plain-language description of a location, kept in step with the radius slider. */
export function describe(loc) {
  if (!loc) return '';
  if (loc.scope === 'country') return 'All of India';
  if (loc.scope === 'regions') return loc.regions.join(', ');
  const names = loc.cities.length > 4 ? `${loc.cities.slice(0, 4).join(', ')} and ${loc.cities.length - 4} more cities` : loc.cities.join(', ');
  return `${names} (${loc.radius_km} km around ${loc.cities.length > 1 ? 'each' : 'it'})`;
}

export default function LocationPicker({ value, homeCity, onChange }) {
  const [mode, setMode] = useState(value ? 'done' : '');
  const [city, setCity] = useState(homeCity || '');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const apply = (loc) => { onChange({ ...loc, summary: describe(loc) }); setMode('done'); setError(''); };

  const preview = async (payload) => {
    setBusy(true); setError('');
    try {
      apply((await api.post('/api/campaigns/locations/preview', { text: payload })).data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "We couldn't understand that location. Try again.");
    }
    setBusy(false);
  };

  const choose = (key) => {
    setError('');
    if (key === 'major_cities' || key === 'whole_india') { preview(key); return; }
    setMode(key);
  };

  const setRadius = (km) => apply({ ...value, radius_km: km });
  const removeName = (name) => {
    const field = value.scope === 'regions' ? 'regions' : 'cities';
    const rest = value[field].filter(n => n !== name);
    if (rest.length) apply({ ...value, [field]: rest, unmatched: (value.unmatched || []).filter(n => n !== name) });
  };

  if (mode === 'done' && value) {
    const names = value.scope === 'regions' ? value.regions : value.cities;
    return (
      <div className="space-y-3" data-testid="location-result">
        <div className="p-4 rounded-xl border-2 border-ink bg-butter shadow-neo-sm">
          <p className="text-xs text-muted-foreground mb-1">Your ad will be shown to people in:</p>
          <p className="text-sm font-semibold">{describe(value)}</p>
          {value.scope !== 'country' && (
            <div className="flex flex-wrap gap-1.5 mt-3 max-h-32 overflow-y-auto">
              {names.map(n => (
                <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs">
                  {n}
                  {names.length > 1 && <button onClick={() => removeName(n)} aria-label={`Remove ${n}`} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>}
                </span>
              ))}
            </div>
          )}
          {value.unmatched?.length > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-3" data-testid="location-unmatched">
              Meta couldn&apos;t find {value.unmatched.join(', ')}. Remove {value.unmatched.length > 1 ? 'them' : 'it'}, check the spelling, or pick a nearby city.
            </p>
          )}
        </div>
        {value.scope === 'cities' && (
          <div>
            <Label className="text-sm font-semibold mb-2 block">Area around {names.length > 1 ? 'each city' : 'the city'}: {value.radius_km} km</Label>
            <Slider value={[value.radius_km]} min={17} max={80} step={1} onValueChange={([km]) => setRadius(km)} data-testid="radius-slider" />
            <p className="text-xs text-muted-foreground mt-1.5">17 km is the smallest area Meta allows around a city.</p>
          </div>
        )}
        <button onClick={() => setMode('')} className="text-xs text-brand-strong underline" data-testid="change-location">Change location</button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="location-picker">
      <Label className="text-sm font-semibold block">Where are your customers?</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OPTIONS.map(o => (
          <button key={o.key} onClick={() => choose(o.key)} disabled={busy}
            className={`p-3 rounded-xl text-left border-2 transition-all ${mode === o.key ? 'border-ink bg-butter shadow-neo-sm' : 'border-ink/20 bg-card hover:border-ink'}`}
            data-testid={`location-option-${o.key}`}>
            <span className="flex items-center gap-1.5 text-sm font-semibold"><o.icon className="w-4 h-4 text-brand-strong" />{o.title}</span>
            <span className="block text-xs text-muted-foreground mt-0.5">{o.help}</span>
          </button>
        ))}
      </div>

      {mode === 'my_city' && (
        <div className="flex gap-2">
          <Input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Bengaluru" data-testid="location-city-input" />
          <Button className="gradient-bg" disabled={!city.trim()}
            onClick={() => apply({ scope: 'cities', cities: [city.trim()], regions: [], radius_km: 25 })}>Use</Button>
        </div>
      )}

      {mode === 'custom' && (
        <div className="space-y-2">
          <Textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="Describe it in your own words" data-testid="location-text-input" />
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map(ex => (
              <button key={ex} onClick={() => setText(ex)} className="px-2 py-0.5 rounded-full border-2 border-ink/25 bg-card text-xs font-medium hover:border-ink hover:bg-butter">{ex}</button>
            ))}
          </div>
          <Button className="gradient-bg" disabled={!text.trim() || busy} onClick={() => preview(text)} data-testid="location-check-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Check this location
          </Button>
        </div>
      )}

      {busy && mode !== 'custom' && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Working it out…</p>}
      {error && <p className="text-xs text-red-700 dark:text-red-400" data-testid="location-error">{error}</p>}
    </div>
  );
}
